import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  FiUsers,
  FiZap,
  FiShoppingCart,
  FiDollarSign,
  FiAward,
  FiClock,
  FiUserCheck,
  FiPackage,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import adminAxios from "../../utils/adminAxios";

/* ── Theming — CSS variables so charts follow light/dark automatically ── */
/* Palette source: dataviz skill reference palette (sequential blue + reserved status hues) */
const VIZ_STYLES = `
.stats-viz {
  --surface-1: #ffffff;
  --text-primary: #0b0b0b;
  --text-secondary: #52514e;
  --muted: #898781;
  --gridline: #e1e0d9;
  --baseline: #c3c2b7;
  --series-1: #2a78d6;
  --series-1-wash: rgba(42, 120, 214, 0.12);
  --good: #0ca30c;
  --warning: #fab219;
  --critical: #d03b3b;
}
@media (prefers-color-scheme: dark) {
  .stats-viz {
    --surface-1: #1f2937;
    --text-primary: #ffffff;
    --text-secondary: #c3c2b7;
    --muted: #9a9992;
    --gridline: #2c2c2a;
    --baseline: #383835;
    --series-1: #3987e5;
    --series-1-wash: rgba(57, 135, 229, 0.18);
    --good: #0ca30c;
    --warning: #fab219;
    --critical: #e66767;
  }
}
`;

const getDayPresets = (t) => [
  { label: t("dashboard.statistics.filters.days7"), value: 7 },
  { label: t("dashboard.statistics.filters.days30"), value: 30 },
  { label: t("dashboard.statistics.filters.days90"), value: 90 },
];

const numberFmt = (n) => Math.round(n || 0).toLocaleString("en-US");

const compactFmt = (n) => {
  const value = n || 0;
  if (Math.abs(value) < 1000) return numberFmt(value);
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
};

const currencyFmt = (n, unit) => `${numberFmt(n)} ${unit}`;

const dateTickFmt = (isoDate) => {
  const [, month, day] = isoDate.split("-");
  return `${day}/${month}`;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const STATUS_LABEL_KEYS = {
  paid: "paid",
  captured: "captured",
  verified: "verified",
  authorized: "authorized",
  initiated: "initiated",
  failed: "failed",
  refunded: "refunded",
  voided: "voided",
  unknown: "unknown",
  Pending: "pending",
  Approved: "approved",
  Rejected: "rejected",
};

const getStatusLabel = (t, status) =>
  t(`dashboard.statistics.status.${STATUS_LABEL_KEYS[status] || "unknown"}`);

/* ── Small building blocks ───────────────────────────────────────────── */

const StatCard = ({ icon, label, value, secondary, accent = "series-1" }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -4 }}
    className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 transition-shadow hover:shadow-xl"
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
          {label}
        </p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">
          {value}
        </p>
        {secondary && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {secondary}
          </p>
        )}
      </div>
      <div
        className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center"
        style={{
          backgroundColor:
            accent === "warning"
              ? "rgba(250, 178, 25, 0.15)"
              : accent === "good"
                ? "rgba(12, 163, 12, 0.12)"
                : "rgba(42, 120, 214, 0.12)",
          color:
            accent === "warning"
              ? "#c98500"
              : accent === "good"
                ? "#0ca30c"
                : "#2a78d6",
        }}
      >
        {icon}
      </div>
    </div>
  </motion.div>
);

const ChartCard = ({ title, subtitle, children, className = "" }) => (
  <motion.div
    variants={itemVariants}
    className={`stats-viz bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 ${className}`}
    style={{ backgroundColor: "var(--surface-1)" }}
  >
    <style>{VIZ_STYLES}</style>
    <div className="mb-4">
      <h3
        className="text-base font-semibold"
        style={{ color: "var(--text-primary)" }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
          {subtitle}
        </p>
      )}
    </div>
    {children}
  </motion.div>
);

const EmptyChartState = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div
      className="h-52 flex items-center justify-center text-sm"
      style={{ color: "var(--muted)" }}
    >
      {message || t("dashboard.statistics.emptyState")}
    </div>
  );
};

const ValueTooltip = ({ active, payload, label, formatter, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg shadow-lg px-3 py-2 text-xs border"
      style={{
        backgroundColor: "var(--surface-1)",
        borderColor: "var(--gridline)",
        color: "var(--text-secondary)",
      }}
    >
      <div className="mb-1" style={{ color: "var(--text-secondary)" }}>
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-0.5 rounded"
            style={{ backgroundColor: entry.color || "var(--series-1)" }}
          />
          <span
            className="font-semibold tabular-nums"
            style={{ color: "var(--text-primary)" }}
          >
            {formatter ? formatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ── Main page ────────────────────────────────────────────────────────── */

const Statistics = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const DAY_PRESETS = getDayPresets(t);
  const currencyUnit = t("dashboard.statistics.currency.riyal");

  const fetchStats = async (selectedDays, isBackground = false) => {
    try {
      if (isBackground) setRefreshing(true);
      else setLoading(true);
      const response = await adminAxios.get("statistics/dashboard", {
        params: { days: selectedDays },
      });
      setData(response.data.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("dashboard.statistics.errors.loadFailed")
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats(days, Boolean(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const consignmentInProgress = useMemo(() => {
    if (!data) return 0;
    const { total, byStatus } = data.overview.consignmentApplications;
    return total - (byStatus.Approved || 0) - (byStatus.Rejected || 0);
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!data) return null;

  const { overview, charts } = data;

  return (
    <div
      className={`space-y-6 transition-opacity duration-300 ${
        refreshing ? "opacity-60" : "opacity-100"
      }`}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.statistics.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.statistics.subtitle")}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
            {DAY_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => setDays(preset.value)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  days === preset.value
                    ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchStats(days, true)}
            disabled={refreshing}
            className="p-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title={t("dashboard.statistics.actions.refresh")}
          >
            <FiRefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
      >
        <StatCard
          icon={<FiUsers className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.totalUsers.label")}
          value={numberFmt(overview.users.total)}
          secondary={t("dashboard.statistics.cards.totalUsers.secondary", {
            count: numberFmt(overview.users.newInPeriod),
            days,
          })}
        />
        <StatCard
          icon={<FiZap className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.totalAuctions.label")}
          value={numberFmt(overview.auctions.total)}
          secondary={t("dashboard.statistics.cards.totalAuctions.secondary", {
            live: numberFmt(overview.auctions.live),
            open: numberFmt(overview.auctions.open),
          })}
        />
        <StatCard
          icon={<FiShoppingCart className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.totalOrders.label")}
          value={numberFmt(overview.orders.total)}
          secondary={t("dashboard.statistics.cards.totalOrders.secondary", {
            count: numberFmt(overview.orders.pending),
          })}
        />
        <StatCard
          icon={<FiDollarSign className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.revenue.label")}
          value={currencyFmt(overview.orders.revenue, currencyUnit)}
          secondary={t("dashboard.statistics.cards.revenue.secondary", {
            count: numberFmt(overview.orders.paid),
          })}
        />
        <StatCard
          icon={<FiAward className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.winningsValue.label")}
          value={currencyFmt(overview.winnings.totalValue, currencyUnit)}
          secondary={t("dashboard.statistics.cards.winningsValue.secondary", {
            sold: numberFmt(overview.winnings.sold),
            unsold: numberFmt(overview.winnings.unsold),
          })}
        />
        <StatCard
          icon={<FiClock className="w-5 h-5" />}
          label={t("dashboard.statistics.cards.pendingRequests.label")}
          value={numberFmt(overview.liveAuctionRequests.pending)}
          secondary={t("dashboard.statistics.cards.pendingRequests.secondary")}
          accent="warning"
        />
        <StatCard
          icon={<FiUserCheck className="w-5 h-5" />}
          label={t(
            "dashboard.statistics.cards.pendingExpertApplications.label"
          )}
          value={numberFmt(overview.expertApplications.byStatus.Pending || 0)}
          secondary={t(
            "dashboard.statistics.cards.pendingExpertApplications.secondary"
          )}
          accent="warning"
        />
        <StatCard
          icon={<FiPackage className="w-5 h-5" />}
          label={t(
            "dashboard.statistics.cards.consignmentInProgress.label"
          )}
          value={numberFmt(consignmentInProgress)}
          secondary={t(
            "dashboard.statistics.cards.consignmentInProgress.secondary"
          )}
          accent="warning"
        />
      </motion.div>

      {/* Users growth — hero chart */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`growth-${days}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-5"
        >
          <ChartCard
            title={t("dashboard.statistics.charts.usersGrowth.title")}
            subtitle={t("dashboard.statistics.charts.usersGrowth.subtitle", {
              days,
            })}
          >
            {charts.usersGrowth.every((d) => d.count === 0) ? (
              <EmptyChartState />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={charts.usersGrowth}>
                  <defs>
                    <linearGradient id="usersGrowthFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--series-1)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="var(--series-1)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="var(--gridline)"
                    strokeDasharray="0"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={dateTickFmt}
                    tick={{ fill: "var(--muted)", fontSize: 12 }}
                    axisLine={{ stroke: "var(--baseline)" }}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "var(--muted)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                  />
                  <Tooltip
                    content={
                      <ValueTooltip
                        formatter={(v) =>
                          t("dashboard.statistics.charts.usersGrowth.tooltip", {
                            count: numberFmt(v),
                          })
                        }
                        labelFormatter={dateTickFmt}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="var(--series-1)"
                    strokeWidth={2}
                    fill="url(#usersGrowthFill)"
                    activeDot={{ r: 4, stroke: "var(--surface-1)", strokeWidth: 2 }}
                    animationDuration={900}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Orders count + revenue — two single-measure charts, never dual-axis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title={t("dashboard.statistics.charts.ordersTrend.title")}
              subtitle={t("dashboard.statistics.charts.ordersTrend.subtitle")}
            >
              {charts.ordersTrend.every((d) => d.count === 0) ? (
                <EmptyChartState />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={charts.ordersTrend}>
                    <CartesianGrid vertical={false} stroke="var(--gridline)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={dateTickFmt}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={{ stroke: "var(--baseline)" }}
                      tickLine={false}
                      minTickGap={24}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <Tooltip
                      cursor={{ fill: "var(--series-1-wash)" }}
                      content={
                        <ValueTooltip
                          formatter={(v) =>
                            t(
                              "dashboard.statistics.charts.ordersTrend.countTooltip",
                              { count: numberFmt(v) }
                            )
                          }
                          labelFormatter={dateTickFmt}
                        />
                      }
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--series-1)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={22}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              title={t("dashboard.statistics.charts.revenueTrend.title")}
              subtitle={t("dashboard.statistics.charts.revenueTrend.subtitle")}
            >
              {charts.ordersTrend.every((d) => d.revenue === 0) ? (
                <EmptyChartState />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={charts.ordersTrend}>
                    <defs>
                      <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--series-1)" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="var(--series-1)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="var(--gridline)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={dateTickFmt}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={{ stroke: "var(--baseline)" }}
                      tickLine={false}
                      minTickGap={24}
                    />
                    <YAxis
                      tickFormatter={compactFmt}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      content={
                        <ValueTooltip
                          formatter={(v) => currencyFmt(v, currencyUnit)}
                          labelFormatter={dateTickFmt}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="var(--series-1)"
                      strokeWidth={2}
                      fill="url(#revenueFill)"
                      activeDot={{ r: 4, stroke: "var(--surface-1)", strokeWidth: 2 }}
                      animationDuration={800}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Categories + payment status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title={t("dashboard.statistics.charts.categories.title")}
              subtitle={t("dashboard.statistics.charts.categories.subtitle")}
            >
              {charts.auctionsByCategory.length === 0 ? (
                <EmptyChartState />
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(charts.auctionsByCategory.length * 34, 120)}
                >
                  <BarChart
                    data={charts.auctionsByCategory}
                    layout="vertical"
                    margin={{ left: 8, right: 16 }}
                  >
                    <CartesianGrid horizontal={false} stroke="var(--gridline)" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="category"
                      width={110}
                      tick={{ fill: "var(--text-secondary)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "var(--series-1-wash)" }}
                      content={
                        <ValueTooltip
                          formatter={(v) =>
                            t("dashboard.statistics.charts.categories.tooltip", {
                              count: numberFmt(v),
                            })
                          }
                        />
                      }
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--series-1)"
                      radius={[0, 4, 4, 0]}
                      maxBarSize={18}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              title={t("dashboard.statistics.charts.paymentStatus.title")}
              subtitle={t(
                "dashboard.statistics.charts.paymentStatus.subtitle"
              )}
            >
              {charts.ordersByStatus.length === 0 ? (
                <EmptyChartState />
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(charts.ordersByStatus.length * 34, 120)}
                >
                  <BarChart
                    data={charts.ordersByStatus.map((d) => ({
                      ...d,
                      label: getStatusLabel(t, d.status),
                    }))}
                    layout="vertical"
                    margin={{ left: 8, right: 16 }}
                  >
                    <CartesianGrid horizontal={false} stroke="var(--gridline)" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: "var(--muted)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={90}
                      tick={{ fill: "var(--text-secondary)", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "var(--series-1-wash)" }}
                      content={
                        <ValueTooltip
                          formatter={(v) =>
                            t(
                              "dashboard.statistics.charts.paymentStatus.tooltip",
                              { count: numberFmt(v) }
                            )
                          }
                        />
                      }
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--series-1)"
                      radius={[0, 4, 4, 0]}
                      maxBarSize={18}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Applications status — semantic status colors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title={t(
                "dashboard.statistics.charts.expertApplications.title"
              )}
              subtitle={t(
                "dashboard.statistics.charts.expertApplications.subtitle"
              )}
            >
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={[
                    {
                      key: "Pending",
                      label: getStatusLabel(t, "Pending"),
                      value: overview.expertApplications.byStatus.Pending || 0,
                    },
                    {
                      key: "Approved",
                      label: getStatusLabel(t, "Approved"),
                      value: overview.expertApplications.byStatus.Approved || 0,
                    },
                    {
                      key: "Rejected",
                      label: getStatusLabel(t, "Rejected"),
                      value: overview.expertApplications.byStatus.Rejected || 0,
                    },
                  ]}
                  layout="vertical"
                  margin={{ left: 8, right: 16 }}
                >
                  <CartesianGrid horizontal={false} stroke="var(--gridline)" />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: "var(--muted)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={90}
                    tick={{ fill: "var(--text-secondary)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={
                      <ValueTooltip
                        formatter={(v) =>
                          t(
                            "dashboard.statistics.charts.expertApplications.tooltip",
                            { count: numberFmt(v) }
                          )
                        }
                      />
                    }
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20} animationDuration={800}>
                    <Cell fill="var(--warning)" />
                    <Cell fill="var(--good)" />
                    <Cell fill="var(--critical)" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title={t(
                "dashboard.statistics.charts.consignmentApplications.title"
              )}
              subtitle={t(
                "dashboard.statistics.charts.consignmentApplications.subtitle"
              )}
            >
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={[
                    {
                      label: t("dashboard.statistics.status.inProgress"),
                      value: consignmentInProgress,
                      key: "progress",
                    },
                    {
                      label: getStatusLabel(t, "Approved"),
                      value: overview.consignmentApplications.byStatus.Approved || 0,
                      key: "approved",
                    },
                    {
                      label: getStatusLabel(t, "Rejected"),
                      value: overview.consignmentApplications.byStatus.Rejected || 0,
                      key: "rejected",
                    },
                  ]}
                  layout="vertical"
                  margin={{ left: 8, right: 16 }}
                >
                  <CartesianGrid horizontal={false} stroke="var(--gridline)" />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: "var(--muted)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={90}
                    tick={{ fill: "var(--text-secondary)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={
                      <ValueTooltip
                        formatter={(v) =>
                          t(
                            "dashboard.statistics.charts.consignmentApplications.tooltip",
                            { count: numberFmt(v) }
                          )
                        }
                      />
                    }
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20} animationDuration={800}>
                    <Cell fill="var(--warning)" />
                    <Cell fill="var(--good)" />
                    <Cell fill="var(--critical)" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Statistics;
